import React, { useMemo } from "react";
import type { Tab } from "@/contexts/tabs-context";
import { Route as WorkspaceRoute } from "@/routes/$workspaceId/route";
import { DynamicView, getViewType } from "@/components/views/registry";
import { CurrentTabProvider } from "@/contexts/current-tab-context";
import { useWorkspaceContext } from "@/contexts/workspace-context";

interface TabContentRendererProps {
	tab: Tab;
	isFocused: boolean;
}

const MissingChannelState: React.FC = () => (
	<div className="flex h-full items-center justify-center">
		<div className="text-center">
			<p className="text-sm font-medium text-foreground">
				This channel doesn&#39;t exist
			</p>
			<p className="mt-2 text-xs text-muted-foreground">
				It may have been archived or deleted.
			</p>
		</div>
	</div>
);

export const TabContentRenderer: React.FC<TabContentRendererProps> = React.memo(
	({ tab, isFocused }) => {
		const { session, currentWorkspace } = WorkspaceRoute.useLoaderData();
		
		const { channels, channelsLoading } = useWorkspaceContext();

		// Extract IDs from the tab
		const workspaceId = tab.workspaceId;
		const channelId = tab.channelId;
		const channel = useMemo(
			() => channels?.find((c) => c.id === Number(channelId)),
			[channels, channelId]
		);
		const isChannelIdNumeric =
			channelId != null && channelId !== "" && Number.isFinite(Number(channelId));
		const isArchived = Boolean(channel?.archived ?? channel?.isArchived);
		const showMissingChannel =
			isChannelIdNumeric && !channelsLoading && (!channel || isArchived);
		
		// Get view type and props for dynamic rendering
		const viewType = useMemo(() => getViewType(tab), [tab]);
		const viewProps = useMemo(
			() => ({
				workspaceId,
				channelId,
				channel,
				session,
				currentWorkspace,
				channels,
			}),
			[workspaceId, channelId, channel, session, currentWorkspace, channels]
		);
		
		// Always use DynamicView - never switch between different rendering methods
		// This eliminates flickering by maintaining consistent rendering
		return (
			<CurrentTabProvider tab={tab}>
				<div className="relative h-full w-full overflow-hidden">
					{showMissingChannel ? (
						<MissingChannelState />
					) : (
						<DynamicView key={tab.id} type={viewType} props={viewProps} />
					)}
				</div>
			</CurrentTabProvider>
		);
	},
	(prevProps, nextProps) => {
		// Custom comparison to prevent unnecessary re-renders
		// Only re-render if essential props change
		return (
			prevProps.tab.id === nextProps.tab.id &&
			prevProps.tab.path === nextProps.tab.path &&
			prevProps.tab.channelId === nextProps.tab.channelId &&
			prevProps.tab.workspaceId === nextProps.tab.workspaceId
		);
	}
);

TabContentRenderer.displayName = 'TabContentRenderer';
